import type { ReactNode } from "react";
import { cn } from "../../lib/utils";
import type { DataGridColumnEntry, DataGridGridLines } from "./types";
import { getVisibleColumnDefs, gridLineClasses, isColumnGroup } from "./types";

export const HEADER_HEIGHT_PX = 65;

type DataGridHeaderProps<TData> = {
	columns: DataGridColumnEntry<TData>[];
	gridTemplate: string;
	labelColumnLabel: string;
	/** When provided, renders in the first header cell in place of the label text. */
	labelColumnHeader?: ReactNode;
	labelColumnHeaderClassName?: string;
	/** When provided, renders in the label column's cell of the grouped sub-header row. Use when the
	 *  label column has its own column headers that must sit alongside the leaf column labels. */
	labelColumnSubHeader?: ReactNode;
	groupBoundaryColumnIds: Set<string>;
	lastColumnId?: string;
	gridLines?: DataGridGridLines;
	/** Header row height in px. Defaults to HEADER_HEIGHT_PX (sized for two-line grouped headers).
	 *  The header block sticks as one unit with any pinned rows; this does not offset a second sticky. */
	headerHeight?: number;
	/** Suppress the grouped sub-label row even when the grid has column groups. */
	hideGroupSubHeader?: boolean;
};

export const DataGridHeader = <TData = unknown>({
	columns,
	gridTemplate,
	labelColumnLabel,
	labelColumnHeader,
	labelColumnHeaderClassName,
	labelColumnSubHeader,
	groupBoundaryColumnIds,
	lastColumnId,
	gridLines = "default",
	headerHeight = HEADER_HEIGHT_PX,
	hideGroupSubHeader = false,
}: DataGridHeaderProps<TData>) => {
	const hasGroups = columns.some(isColumnGroup);
	const showGroupSubHeader = hasGroups && !hideGroupSubHeader;
	const { innerBorder, groupBorder, cellBorderClass } = gridLineClasses(
		gridLines,
		groupBoundaryColumnIds,
		lastColumnId,
	);

	return (
		<>
			<div
				data-testid="datagrid-header"
				className={cn("grid w-max min-w-full bg-surface-base border-b", groupBorder)}
				style={{
					gridTemplateColumns: gridTemplate,
					minHeight: `${headerHeight}px`,
				}}
			>
				<div
					aria-label={labelColumnHeader ? labelColumnLabel : undefined}
					className={cn(
						"sticky left-0 z-10 bg-surface-base border-r flex items-center",
						labelColumnHeader
							? cn("justify-start", labelColumnHeaderClassName ?? "pl-8 pr-4")
							: "px-4 py-2 text-xs font-semibold uppercase tracking-wide text-tertiary text-center justify-center",
						groupBorder,
					)}
				>
					{labelColumnHeader ?? labelColumnLabel}
				</div>
				{columns.map((entry) => {
					if (isColumnGroup(entry)) {
						const visibleCols = entry.columns.filter((c) => c.visible !== false);
						if (visibleCols.length === 0) return null;
						const lastVisibleCol = visibleCols[visibleCols.length - 1];
						return (
							<div
								key={entry.id}
								className={cn("bg-surface-base", cellBorderClass(lastVisibleCol.id))}
								style={{ gridColumn: `span ${visibleCols.length}` }}
							>
								{entry.renderGroupHeader ? (
									entry.renderGroupHeader({ groupId: entry.id })
								) : (
									<div className="flex flex-col items-center justify-center px-4 py-3 h-full">
										<span className="text-sm font-semibold text-primary text-center">
											{entry.label}
										</span>
										{entry.subLabel && (
											<span className="text-xs text-tertiary">{entry.subLabel}</span>
										)}
									</div>
								)}
							</div>
						);
					}
					if (entry.visible === false) return null;
					return (
						<div
							key={entry.id}
							className={cn(
								"bg-surface-base border-r flex items-center justify-center px-4 py-2",
								innerBorder,
							)}
						>
							{entry.renderHeader ?? (
								<span className="text-sm font-semibold text-primary">{entry.label}</span>
							)}
						</div>
					);
				})}
			</div>
			{showGroupSubHeader && (
				<div
					data-testid="datagrid-sub-header"
					className={cn(
						"grid w-max min-w-full bg-surface-light border-b text-xs font-medium text-secondary uppercase tracking-wide",
						groupBorder,
					)}
					style={{
						gridTemplateColumns: gridTemplate,
					}}
				>
					<div
						className={cn(
							"sticky left-0 z-10 bg-surface-light py-2 border-r",
							labelColumnSubHeader ? "px-2" : "px-4",
							groupBorder,
						)}
					>
						{labelColumnSubHeader}
					</div>
					{getVisibleColumnDefs(columns).map((col) => (
						<div
							key={col.id}
							className={cn("bg-surface-light px-2 py-2 text-center", cellBorderClass(col.id))}
						>
							{col.renderHeader ?? col.label}
						</div>
					))}
				</div>
			)}
		</>
	);
};
